
import { useState, useEffect, useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box, Container, CssBaseline } from '@mui/material';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';// , Switch
import { ThemeModeContext } from "./contexts/ThemeModeContext";
import RoutesList from './constants/Routes';
import type { IRoute } from './interfaces/IRoute';

import Header from './components/Header';
import Footer from './components/Footer';
import './css/koffi.scss';

export default function App() {


  // Détecter le thème par défaut du navigateur
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

  // Charger préférence utilisateur si existante, sinon fallback au système
  const readInitial = () => {
    const stored = localStorage.getItem("preferredThemeMode");
    if (stored === "dark") return true;
    if (stored === "light") return false;
    return mediaQuery.matches;
  };
  
  const [darkMode, setDarkMode] = useState<boolean>(readInitial);

  // Écouter les changements du thème système (si l’utilisateur n’a pas forcé une préférence)
  useEffect(() => {
    const handler = (e: MediaQueryListEvent) => {
      // Ne pas écraser une préférence explicite; ici on choisit de détecter si aucune clé en localStorage
      const stored = localStorage.getItem("preferredThemeMode");
      if (!stored) setDarkMode(e.matches);
    };
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [mediaQuery]);

  // Exposer une action pour basculer le thème + persister
  const toggleTheme = () => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem("preferredThemeMode", next ? "dark" : "light");
      return next;
    });
  };

  // Créer le thème avec transition fluide
  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
        },
        palette: {
          mode: darkMode ? "dark" : "light",
          background: {
            default: darkMode ? "rgba(50,50,50,1)" : "rgba(255,255,255,.6)",
            paper: darkMode ? "rgba(50,0,50,.6)" : "rgba(255,255,255,.6)"
          },
          text: {
            primary: !darkMode ? "rgba(0,0,0,1)" : "rgba(255,255,255,1)"
          }
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                backgroundColor: darkMode ? '#CA8E4C' : '#CA8E4C',
                color: darkMode ? '#000000' : '#ffffff',
                padding: '.9rem',
                borderRadius: '10px',
                fontSize: '1.2rem',
                width: '150px',
                textTransform: 'none',
              },
            },
          },
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                transition: "background-color 0.3s ease, color 0.3s ease",
              }
            },
          },
        },
      }),
    [darkMode]
  );

  const themeModeContextValue = useMemo(
    () => ({ darkMode, setDarkMode, toggleTheme }),
    [darkMode]
  );

  return (
    <div id="app">
      <Router>
        <ThemeModeContext.Provider value={themeModeContextValue} >
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Container maxWidth={false} disableGutters>
              <Header />
              <div id="content">
                <Routes>
                  {RoutesList.map((route: IRoute) => <Route path={route.url} key={route.url} element={<route.comp />} />)}
                </Routes>
              </div>

            </Container>
            {/* Footer en bas à droite */}
            <Footer />

            {/* Footer en bas à droite */}
            <Box
              sx={{
                position: "fixed",
                bottom: 20,
                right: 20,
                fontSize: "2rem",
                cursor: "pointer",
                
              }}
              onClick={() => setDarkMode(!darkMode)}
            >
              {darkMode ? "🌙" : "🌞"}
            </Box>

          </ThemeProvider>
        </ThemeModeContext.Provider>
      </Router>
    </div>
  );
}